import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "../components/Navbar";

// À placer dans frontend/src/pages/SpecialtyWilaya.js
// Route : /specialite/:specialtySlug/:wilayaSlug

const API = process.env.REACT_APP_BACKEND_URL || "";

// Beaucoup d'adresses importees se terminent par une virgule
// orpheline : un complement vide concatene a la saisie.
const cleanAddr = (v) => (v || "").trim().replace(/[,;\-\s]+$/, "");

export default function SpecialtyWilaya() {
  const { specialtySlug, wilayaSlug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    fetch(`${API}/api/seo/specialite/${specialtySlug}/${wilayaSlug}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (!d.found) {
          setState("notfound");
          return;
        }
        setData(d);
        setState("ready");
      })
      .catch(() => !cancelled && setState("error"));
    return () => {
      cancelled = true;
    };
  }, [specialtySlug, wilayaSlug]);

  if (state === "loading") {
    return (
      <><Navbar /><div className="max-w-4xl mx-auto px-4 py-10">
        <div className="h-8 w-64 bg-slate-100 rounded animate-pulse mb-6" />
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div></>
    );
  }

  if (state !== "ready") {
    return (
      <><Navbar /><div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold text-slate-900">Page introuvable</h1>
        <p className="mt-2 text-slate-600">
          Cette spécialité ou cette wilaya n'existe pas sur Tabbibak.
        </p>
        <Link to="/search" className="mt-6 inline-block text-sky-600 hover:underline">
          Chercher un médecin
        </Link>
      </div></>
    );
  }

  const { specialty_singular, specialty_plural, wilaya, count, doctors } = data;
  const canonical = `https://tabbibak.com/specialite/${specialtySlug}/${wilayaSlug}`;
  const title = `${specialty_singular} à ${wilaya} — Prendre rendez-vous en ligne | Tabbibak`;
  const description = `${count} ${specialty_plural.toLowerCase()} à ${wilaya} sur Tabbibak. Adresse, téléphone et réservation de rendez-vous en ligne, gratuitement et 24h/24.`;

  return (
    <><Navbar /><div className="max-w-4xl mx-auto px-4 py-8">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <nav className="text-sm text-slate-500 mb-4">
        <Link to="/" className="hover:text-sky-600">Accueil</Link>
        <span className="mx-2">›</span>
        <span className="text-slate-700">{specialty_singular}</span>
        <span className="mx-2">›</span>
        <span className="text-slate-900 font-medium">{wilaya}</span>
      </nav>

      <h1 className="text-3xl font-bold text-slate-900">
        {specialty_singular} à {wilaya}
      </h1>
      <p className="mt-3 text-slate-600 leading-relaxed">
        {count} {specialty_plural.toLowerCase()} référencés à {wilaya}. Consultez
        les adresses et les créneaux disponibles, puis réservez en ligne sans
        passer par le secrétariat.
      </p>

      <ul className="mt-8 space-y-3">
        {doctors.map((d) => {
          const href = d.slug ? `/medecin/${d.slug}` : `/doctor/${d.doctor_id}`;
          const city = d.daira || d.commune;
          return (
            <li key={d.slug || d.doctor_id}>
              <Link
                to={href}
                className="block rounded-xl border border-slate-200 p-4 hover:border-sky-300 hover:bg-sky-50/40 transition-colors"
              >
                <span className="font-semibold text-slate-900">{d.name}</span>
                {city && <span className="block text-sm text-slate-600 mt-1">{city}</span>}
                {cleanAddr(d.address) && (
                  <span className="block text-sm text-slate-500">{cleanAddr(d.address)}</span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div></>
  );
}
